import {type CellAddress, type ItemId, useBoardCard} from '@github-ui/board-core'
import {useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import styles from './BoardCardFrame.module.css'
import {BoardCardMenu} from './BoardCardMenu'

/**
 * Where a card sits in its cell's cards, for a cell that draws only some of them.
 *
 * One value rather than two props, because the two numbers are only ever right together.
 * A position stated without a size is worse than neither: assistive technology resolves it
 * against the items it can see, which in a windowed list is exactly the wrong denominator,
 * so a card would be announced as the third of twelve rather than the two hundredth of
 * eight hundred.
 */
export type BoardCardPlace = {
  /** Zero-based index among the cards the cell has loaded. */
  index: number
  /**
   * How many cards are in the set, as `aria-setsize` means it: every card the cell has,
   * not every card it has loaded, and `-1` when the total is not known.
   *
   * A cell pages, so those are usually different numbers. `BoardCellFrame` works this out
   * from the count the caller stated for the cell; a caller assembling its own does the
   * same.
   */
  setSize: number
  /**
   * Registers this card with its cell's window, which measures it rather than assuming a
   * height. The window reads {@link BoardCardPlace.index} back off the element to know
   * which card it measured, which is why the two arrive together.
   */
  measureRef?: (node: Element | null) => (() => void) | undefined
}

/** The menu a card draws in its top corner. */
export type BoardCardFrameMenu = {
  /**
   * Names the button that opens the menu. `BoardCellFrame` builds it from
   * `labels.cardActions` and the item's own label.
   */
  label: string
  /**
   * The caller's own menu content, such as `ActionList.Item`s, shown above the move
   * controls.
   */
  actions?: ReactNode
}

export type BoardCardFrameProps = {
  itemId: ItemId
  /** The cell this card is in. The same item may sit in two cells on a multi-value board. */
  address: CellAddress
  /**
   * Where this card sits among the cell's cards, and how many there are.
   *
   * Stated rather than left to the browser because a cell windows its cards: only the ones
   * on screen are in the page, so a cell holding five hundred would otherwise announce each
   * card as one of the twelve that happen to be drawn.
   *
   * Optional, because a caller assembling its own cell out of these frames may window
   * nothing, and a list that draws every one of its items needs neither number.
   */
  place?: BoardCardPlace
  /**
   * Draws the line marking where a held card would land, on the edge of this card named.
   *
   * Set on one card of a cell at a time, by whatever is drawing the cell, from
   * `useBoardCell`'s `dropIndex`. `after` is only ever the last card the cell has drawn;
   * every other position is `before` the card it names.
   *
   * Nothing is announced for it. The line answers a pointer gesture, and the reader it is
   * addressed to is the one watching a card follow their pointer.
   */
  insertion?: 'before' | 'after'
  /**
   * Draws a menu button in the card's top corner, whose menu holds the moves the board
   * offers for this card and then `actions`. Absent draws no menu.
   *
   * `BoardCellFrame` passes one for every card on a board that moves cards, and for every
   * card the caller gave actions.
   */
  menu?: BoardCardFrameMenu
  children?: ReactNode
  className?: string
}

/**
 * A card's shell: border, radius, padding, and the menu in its top corner.
 *
 * It is a component rather than something the cell renders inline because it calls its
 * own hook, and a hook cannot be called inside a callback that runs once per card. That
 * is how the roving tabindex, the selection gesture and the drag source all arrived
 * without any caller changing.
 */
export function BoardCardFrame({itemId, address, place, insertion, menu, children, className}: BoardCardFrameProps) {
  const {cardProps, isSelected, isDragging, isDraggable, hasLanded, reportDialogOpen} = useBoardCard(itemId, address)
  const ref = useMergedRefs(cardProps.ref, place?.measureRef)

  return (
    <li
      {...cardProps}
      ref={ref}
      // Read back by the cell's window to know which card a measurement came from. It is
      // here rather than in `cardProps` because it describes where this element sits in a
      // drawn list, which is the styled layer's business rather than the headless one's.
      data-index={place?.index}
      // A styling hook and nothing else. This package draws a selected card no differently
      // from any other, because selection has no treatment yet and inventing one is not
      // this release's to make; a caller that acts on a selection styles this in the
      // meantime. Absent rather than `false`, so `[data-selected]` matches the selected
      // cards and only those.
      data-selected={isSelected ? '' : undefined}
      // The card a pointer has hold of, which this package rings and leaves exactly where
      // it is: `BoardDraggedCard` draws the copy that follows the pointer. On from the
      // press, so the ring answers the grab rather than the movement after it. Absent
      // rather than `false`, on the same rule `data-selected` follows above.
      data-dragging={isDragging ? '' : undefined}
      // The card that cannot be picked up, which stops it offering a `cursor: grab` it
      // cannot honour and gives a caller somewhere to say more. Absent rather than `false`,
      // on the same rule the two above follow.
      //
      // `isDraggable` rather than `dragRefusal`, because the cursor answers whether the
      // gesture exists and the refusal answers why the caller withheld it, and those come
      // apart: a card with no id and every card on a board with no `onItemsMove` cannot be
      // picked up and carries no reason for it. Keyed on the reason, each of those would
      // offer a grab that goes nowhere.
      //
      // One attribute rather than a `data-draggable` beside it: two attributes describing
      // one boolean is two things to keep in step. The negative is the one that carries a
      // fact, and `dragRefusal` is still what a caller reads to say what the reason was,
      // where there is one.
      data-drag-disabled={isDraggable ? undefined : ''}
      // Which edge of this card the insertion line is drawn on, or absent because no drop
      // is being aimed here. The value carries the edge rather than a bare presence,
      // because the end of a cell is a position too and it is the only one drawn below a
      // card rather than above one.
      data-drop-insertion={insertion}
      // The card a drop just moved here, which pulses once. Absent rather than `false`, on
      // the same rule the attributes above follow.
      data-landed={hasLanded ? '' : undefined}
      // Room at the inline end for the menu button, so the card's content does not run
      // under it.
      data-card-menu={menu === undefined ? undefined : ''}
      // Both or neither, which is what makes them one prop.
      aria-posinset={place === undefined ? undefined : place.index + 1}
      aria-setsize={place?.setSize}
      className={clsx(styles.CardFrame, className)}
    >
      {children}
      {/*
        After the content, so a reader hears the card before the button that acts on it,
        and drawn in the top corner by the stylesheet.
      */}
      {menu === undefined ? null : (
        <BoardCardMenu
          itemId={itemId}
          address={address}
          label={menu.label}
          actions={menu.actions}
          isTabStop={cardProps.tabIndex === 0}
          reportDialogOpen={reportDialogOpen}
        />
      )}
    </li>
  )
}
